/** @jsxImportSource @emotion/react */
/**
 * AssistantChatView — this app's mount of the shared Nucleus dock
 * (`NucleusDock` from @atomic/ui-kit, one implementation for every client).
 *
 * Only what THIS app knows lives here: the gateway calls (transport), the
 * router, the ROOT gate, the route registry + tool vocabulary, the report
 * open on the page, the run the page is showing (NUC-258), the reports and
 * data sets behind `@` (NUC-217), the unsaved-work registry and the Plan
 * Review hand-off. Everything the user sees — the decision trace, the
 * streamed answer with sources and follow-ups, the prompt bar, the download
 * card, the turn id, Action Taken, past chats, the model control, the
 * confirmation modal — is the kit's (2026-09-15: LMNT's own implementation of
 * all of that moved into ui-kit so no client lags another).
 */
import { useApolloClient } from "@apollo/client/react";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import {
  NucleusDock,
  demoTurnPolls,
  openPlanReviewHandoff,
  type NucleusHost,
  type NucleusReferences,
  type NucleusTransport,
} from "@atomic/ui-kit";
import {
  ASSISTANT_CALL,
  ASSISTANT_CHAT_POLL,
  ASSISTANT_CHAT_START,
  ASSISTANT_RESOLVE_CHANGE,
  ASSISTANT_SUGGESTIONS,
} from "../../services/backend_gateway/mutations/assistant";
import { ROUTE_REGISTRY } from "../../routeRegistry";
import { TOOL_VARIABLES } from "../../nucleusToolVariables";
import { activeReportFromLocation } from "../../nucleusActiveReport";
import { activeRunFromSelection, pageShowsRunSelector } from "../../nucleusActiveRun";
import { useRunSelection } from "../../hooks/runSelectionContext";
import { runSelectorLabel } from "../../hooks/runSelectionState";
import { useAuth } from "../../hooks/useAuth";
import { describePendingChanges, hasPendingChanges } from "../../pendingChanges";
import { ListQueryvizReportsDocument, type ListQueryvizReportsQuery } from "../../services/__generated__/graphql";
import { GET_MY_PULL_SOURCES } from "../../hooks/useMappingGaps";
import { QUERYVIZ_ORG_SLUG, QUERYVIZ_PROJECT_SLUG } from "../ReportingView/constants";
import { parseCategory } from "../ReportingView/category";

/** Reports marked internal-only in their notes stay off the @ list for non-root users (as on the Reporting page). */
const INTERNAL_ONLY_RE = /#internalonly/i;

interface PullSourceRow {
  name: string;
  displayName?: string | null;
  description?: string | null;
}

export const AssistantChatView: FC<{
  currentViewLabel: string;
  /** Bumped by the host each time the dock is opened (icon, shortcut, deep
   *  link). The view stays mounted while hidden, so mount-time autoFocus
   *  can't work — the host signals "you just became visible" instead. */
  focusRequest?: number;
  /** Bumped by the dock header's "Clear conversation": drop the history and
   *  start a fresh conversation, keeping the dock open. */
  resetRequest?: number;
  /** Whether the dock is showing. It stays mounted while hidden, so the
   *  composer needs to know to stop a live dictation (NUC-217). */
  open?: boolean;
}> = ({ currentViewLabel, focusRequest = 0, resetRequest = 0, open = true }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const apollo = useApolloClient();
  // Same ROOT gate as the dock itself (DashboardLayout / Sidebar).
  const isRoot = user?.role === "ROOT";

  // The gateway calls, each returning core's JSON string untouched.
  const transport = useMemo<NucleusTransport>(
    () => ({
      suggestions: async (uiContext) => {
        const { data } = await apollo.query<{ assistantSuggestions: string }>({
          query: ASSISTANT_SUGGESTIONS,
          variables: { uiContext },
          fetchPolicy: "network-only",
        });
        return data?.assistantSuggestions ?? "";
      },
      startTurn: async (input) => {
        const { data } = await apollo.mutate<{ assistantChatStart: string }>({
          mutation: ASSISTANT_CHAT_START,
          variables: input,
        });
        return data?.assistantChatStart ?? "";
      },
      pollTurn: async (requestId) => {
        const { data } = await apollo.query<{ assistantChatPoll: string }>({
          query: ASSISTANT_CHAT_POLL,
          variables: { requestId },
          fetchPolicy: "network-only",
        });
        return data?.assistantChatPoll ?? "";
      },
      resolveChange: async (changeId, action) => {
        const { data } = await apollo.mutate<{ assistantResolveChange: string }>({
          mutation: ASSISTANT_RESOLVE_CHANGE,
          variables: { changeId, action },
        });
        return data?.assistantResolveChange ?? "";
      },
      // The generic passthrough (past chats): the gateway prefixes the op with
      // core's assistant surface and injects the verified actor.
      call: async (op, payload) => {
        const { data } = await apollo.mutate<{ assistantCall: string }>({
          mutation: ASSISTANT_CALL,
          variables: { op, payload: JSON.stringify(payload) },
        });
        return data?.assistantCall ?? "";
      },
    }),
    [apollo],
  );

  // The run the page is showing (NUC-258). Read from the same context the Data
  // selector writes, not passed down: the dock is mounted inside
  // RunSelectionProvider, so this cannot disagree with what is on screen, and
  // a selector change mid-conversation is picked up on the next send. A prop
  // chain would go stale exactly then.
  const { selectedRun, sortedRuns } = useRunSelection();
  const activeRun = useMemo(() => {
    if (!selectedRun) return null;
    return activeRunFromSelection({
      run: selectedRun,
      // Whether the planner could have chosen this run on the page they are
      // asking from; the provider resolves one app-wide either way.
      selectedOnPage: pageShowsRunSelector(pathname),
      label: runSelectorLabel(selectedRun, { latestRunId: sortedRuns[0]?.id ?? null }),
    });
  }, [selectedRun, sortedRuns, pathname]);
  // Read at send time (the dock calls extraUiContext then), never captured.
  const activeRunRef = useRef(activeRun);
  activeRunRef.current = activeRun;

  // What this app offers behind `@` (NUC-217): the QueryViz reports and the
  // master-data pull sources. The dock asks on the first `@`; both queries
  // are cache-first and cheap, and a failure just leaves that source out.
  const references = useCallback(async (): Promise<NucleusReferences> => {
    const [reports, pulls] = await Promise.all([
      apollo
        .query<ListQueryvizReportsQuery>({
          query: ListQueryvizReportsDocument,
          variables: { orgSlug: QUERYVIZ_ORG_SLUG, projectSlug: QUERYVIZ_PROJECT_SLUG },
          fetchPolicy: "cache-first",
        })
        .then(({ data }) => data?.listQueryvizReports ?? [])
        .catch(() => []),
      apollo
        .query<{ getMyPullSources: PullSourceRow[] }>({ query: GET_MY_PULL_SOURCES, fetchPolicy: "cache-first" })
        .then(({ data }) => data?.getMyPullSources ?? [])
        .catch(() => []),
    ]);
    return {
      reports: reports.flatMap((r) =>
        r && typeof r.name === "string" && typeof r.slug === "string" && (isRoot || !INTERNAL_ONLY_RE.test(r.notes ?? ""))
          ? [{ name: r.name, slug: r.slug, category: r.category ? parseCategory(r.category)?.display ?? r.category : null }]
          : [],
      ),
      datasets: pulls.flatMap((d) =>
        d && typeof d.name === "string"
          ? [{ name: d.name, displayName: d.displayName ?? null, description: d.description ?? null }]
          : [],
      ),
    };
  }, [apollo, isRoot]);

  // The embed freezes its report AND params at mount and keys only on the
  // tab's base path — so a redirect to another report under the same tab
  // changed the URL and nothing else. Always stamp the resetEmbed nonce the
  // sidebar uses to force one clean remount; on a genuinely new route it is
  // simply unused.
  const go = (url: string) => navigate(url, { state: { resetEmbed: Date.now() } });

  const host = useMemo<NucleusHost>(
    () => ({
      pathname,
      isRoot,
      routeRegistry: ROUTE_REGISTRY,
      toolVariables: TOOL_VARIABLES,
      // Read from the LIVE location: in-frame edits rewrite the URL without a
      // re-render of this closure (nucleusActiveReport.ts).
      activeReport: () => activeReportFromLocation(window.location),
      // `active_run` is omitted rather than sent as null when there is no
      // selection, so the model is never handed a run-shaped blank to reason
      // from (uiContext.ts, retired 2026-09-15, kept the same rule).
      extraUiContext: () => (activeRunRef.current ? { active_run: activeRunRef.current } : {}),
      references,
      navigate: go,
      openPlanReview: openPlanReviewHandoff({ navigate: go }),
      pendingChanges: { has: hasPendingChanges, describe: describePendingChanges },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname, isRoot, references],
  );

  // Deep link: `?prompt=` is handed to the dock (sent once per nonce) and
  // consumed from the URL so a refresh (or the embed's own URL mirroring)
  // can't re-send it.
  const [searchParams, setSearchParams] = useSearchParams();
  const [promptRequest, setPromptRequest] = useState<{ text: string; nonce: number } | null>(null);
  const promptSeq = useRef(0);
  useEffect(() => {
    const prompt = searchParams.get("prompt");
    if (!prompt) return;
    const next = new URLSearchParams(searchParams);
    next.delete("prompt");
    setSearchParams(next, { replace: true });
    setPromptRequest({ text: prompt, nonce: ++promptSeq.current });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // DEV only: `?nucleus_demo=reasoning|error|concurrent|download|html` replays
  // a recorded turn through the dock's own reducer instead of the gateway, so
  // the trace and cards can be checked without a backend. Never in production.
  const replay = useMemo(() => {
    if (!import.meta.env.DEV) return null;
    const name = searchParams.get("nucleus_demo");
    return name ? demoTurnPolls(name) ?? null : null;
  }, [searchParams]);

  return (
    <NucleusDock
      transport={transport}
      host={host}
      currentViewLabel={currentViewLabel}
      focusRequest={focusRequest}
      resetRequest={resetRequest}
      promptRequest={promptRequest}
      open={open}
      replay={replay}
    />
  );
};
